import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { BRAND_MINT } from '@/lib/utils'
import {
  getAdminDemoRequestsApi,
  updateDemoRequestStatusApi,
  type DemoRequest,
} from '@/lib/api'
import {
  LoaderIcon,
  SearchIcon,
  FilterIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MailIcon,
  BuildingIcon,
  UsersIcon,
  ClockIcon,
  MessageSquareIcon,
  CheckCircleIcon,
  InboxIcon,
} from 'lucide-react'

const STATUS_STYLES: Record<string, { label: string; color: string }> = {
  new: { label: 'New', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  contacted: { label: 'Contacted', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  converted: { label: 'Converted', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-muted text-muted-foreground' },
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
]

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export const Route = createFileRoute('/dashboard/admin/demo-requests')({
  component: () => {
    const [requests, setRequests] = useState<DemoRequest[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(50)
    const [loading, setLoading] = useState(true)
    const [initialLoaded, setInitialLoaded] = useState(false)

    // Filters
    const [statusFilter, setStatusFilter] = useState('')
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [showFilters, setShowFilters] = useState(false)

    // Debounce search input so we don't fire an API call on every keystroke
    useEffect(() => {
      const t = setTimeout(() => setDebouncedSearch(search), 400)
      return () => clearTimeout(t)
    }, [search])

    // Detail drawer
    const [detail, setDetail] = useState<DemoRequest | null>(null)
    const [showDetail, setShowDetail] = useState(false)

    // Status update
    const [updating, setUpdating] = useState(false)
    const [confirmStatus, setConfirmStatus] = useState<string | null>(null)

    const totalPages = Math.max(1, Math.ceil(total / limit))

    const buildParams = useCallback(() => {
      const params: Record<string, string | number> = { page, limit }
      if (statusFilter) params.status = statusFilter
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim()
      return params
    }, [page, limit, statusFilter, debouncedSearch])

    useEffect(() => {
      let cancelled = false
      async function load() {
        setLoading(true)
        try {
          const res = await getAdminDemoRequestsApi(buildParams())
          if (!cancelled) { setRequests(res.data); setTotal(res.total); setInitialLoaded(true) }
        } catch (err) {
          if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load demo requests')
        } finally { if (!cancelled) setLoading(false) }
      }
      load()
      return () => { cancelled = true }
    }, [buildParams])

    const clearFilters = () => { setStatusFilter(''); setSearch(''); setDebouncedSearch(''); setPage(1) }
    const hasFilters = !!statusFilter || !!search.trim()

    const openDetail = (req: DemoRequest) => {
      setDetail(req)
      setShowDetail(true)
    }

    const closeDetail = () => { setShowDetail(false); setDetail(null); setConfirmStatus(null) }

    const handleStatusUpdate = async (newStatus: string) => {
      if (!detail) return
      setUpdating(true)
      setConfirmStatus(null)
      try {
        await updateDemoRequestStatusApi(detail.demo_request_id, newStatus as 'new' | 'contacted' | 'converted' | 'rejected')
        setRequests((prev) => prev.map((r) => r.demo_request_id === detail.demo_request_id ? { ...r, status: newStatus } : r))
        setDetail({ ...detail, status: newStatus })
        toast.success(`Status updated to "${newStatus}".`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update status')
      } finally { setUpdating(false) }
    }

    // Initial loading state
    if (loading && !initialLoaded) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Demo requests</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading demo requests…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Demo requests</h1>
                <p className="mt-1 text-sm text-muted-foreground">Track and manage landing page demo form submissions.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5 shrink-0">
                <FilterIcon className="size-4" />{showFilters ? 'Hide filters' : 'Filters'}
                {hasFilters && <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">!</span>}
              </Button>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div className="mb-6 rounded-xl border border-border bg-card p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                  className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary">
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Search</label>
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                    placeholder="Name, email, company…"
                    className="w-full rounded-lg border border-border bg-background py-1.5 pl-9 pr-3 text-sm outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div className="flex items-end">
                {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="gap-1.5"><XIcon className="size-4" />Clear</Button>}
              </div>
            </div>
          </div>
        )}

        {/* Count */}
        <p className="mb-4 text-xs text-muted-foreground">
          {total} request{total !== 1 ? 's' : ''}{page > 1 && ` — page ${page} of ${totalPages}`}
        </p>

        {/* List */}
        {requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <InboxIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No demo requests found</p>
            <p className="text-sm text-muted-foreground">{hasFilters ? 'No requests match the current filters.' : 'No demo form submissions yet.'}</p>
            {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2 gap-1.5"><XIcon className="size-4" />Clear filters</Button>}
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((req) => {
              const statusInfo = STATUS_STYLES[req.status] ?? { label: req.status, color: 'bg-muted text-muted-foreground' }
              return (
                <button key={req.demo_request_id} type="button" onClick={() => openDetail(req)}
                  className="w-full rounded-xl border border-border bg-card p-4 text-left transition-shadow hover:shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <MailIcon className="size-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">{req.full_name}</span>
                        <span className="ml-1 shrink-0 rounded-md bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">#{req.demo_request_id}</span>
                        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <MailIcon className="size-3" />
                          {req.email}
                        </span>
                        {req.company_name && (
                          <span className="inline-flex items-center gap-1">
                            <BuildingIcon className="size-3" />
                            {req.company_name}
                          </span>
                        )}
                        {req.employee_count != null && (
                          <span className="inline-flex items-center gap-1">
                            <UsersIcon className="size-3" />
                            {req.employee_count} employees
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <ClockIcon className="size-3" />
                          {formatDate(req.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1.5"><ChevronLeftIcon className="size-4" />Previous</Button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1.5">Next<ChevronRightIcon className="size-4" /></Button>
          </div>
        )}

        {/* Detail Drawer */}
        {showDetail && detail && (
          <>
            <button type="button" aria-label="Close detail" className="fixed inset-0 z-40 bg-black/30" onClick={closeDetail} />
            <aside aria-label="Demo request details" className="fixed inset-y-0 right-0 z-50 w-full max-w-lg border-l border-border bg-background shadow-xl motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="text-base font-semibold text-foreground">Demo request details</h2>
                <button type="button" onClick={closeDetail} className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Close">
                  <XIcon className="size-4" />
                </button>
              </div>
              <div className="overflow-y-auto p-5" style={{ height: 'calc(100vh - 65px)' }}>
                <div className="space-y-6">
                  {/* Status badge + actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><MailIcon className="size-6" /></div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">{detail.full_name}</h3>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${(STATUS_STYLES[detail.status] ?? { color: 'bg-muted text-muted-foreground' }).color}`}>
                          {STATUS_STYLES[detail.status]?.label ?? detail.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    <DetailField icon={<MailIcon className="size-4" />} label="Email" value={detail.email} />
                    {detail.company_name && <DetailField icon={<BuildingIcon className="size-4" />} label="Company" value={detail.company_name} />}
                    {detail.employee_count != null && <DetailField icon={<UsersIcon className="size-4" />} label="Employees" value={String(detail.employee_count)} />}
                    <DetailField icon={<ClockIcon className="size-4" />} label="Submitted" value={formatDate(detail.created_at)} />
                  </div>

                  {/* Message */}
                  {detail.message && (
                    <div className="border-t border-border pt-4">
                      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                        <MessageSquareIcon className="size-3.5" />Message
                      </h4>
                      <p className="rounded-lg bg-muted/50 p-3 text-sm text-foreground whitespace-pre-wrap">{detail.message}</p>
                    </div>
                  )}

                  {/* Status actions */}
                  <div className="border-t border-border pt-4">
                    <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Update status</h4>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((opt) => {
                        const isActive = detail.status === opt.value
                        return (
                          <Button
                            key={opt.value}
                            variant={isActive ? 'default' : 'outline'}
                            size="sm"
                            disabled={updating || isActive}
                            onClick={() => setConfirmStatus(opt.value)}
                            className="gap-1.5"
                          >
                            {opt.value === 'converted' && <CheckCircleIcon className="size-3.5" />}
                            {opt.label}
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Status update confirmation */}
            <Dialog
              open={confirmStatus !== null}
              onClose={() => setConfirmStatus(null)}
              title={`Mark as "${confirmStatus}"?`}
              description={`Update this demo request from ${detail.full_name} to "${confirmStatus}" status.`}
              footer={
                <>
                  <Button variant="outline" size="sm" onClick={() => setConfirmStatus(null)}>Cancel</Button>
                  <Button
                    size="sm"
                    onClick={() => confirmStatus && handleStatusUpdate(confirmStatus)}
                    disabled={updating}
                    className="gap-1.5"
                  >
                    {updating ? <LoaderIcon className="size-4 animate-spin" /> : <CheckCircleIcon className="size-4" />}
                    Confirm
                  </Button>
                </>
              }
            />
          </>
        )}
      </div>
    )
  },
})

function DetailField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
        <p className="truncate text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  )
}
